/**
 * CSV writer for the watchlist export matrix (PV18, SE-8360): UTF-8 with
 * BOM so spreadsheets read accented names, CRLF records and RFC 4180
 * quoting.
 * @module features/scouting/utils/watchlistExport/toCsv
 */

import type { ExportMatrix } from "./exportMatrix";

/** Byte-order mark spreadsheets need to read the file as UTF-8. */
const UTF8_BOM = "﻿";

/** RFC 4180 record terminator. */
const CRLF = "\r\n";

/**
 * Quotes one field when it carries a separator, a quote or a line break;
 * an embedded quote is doubled.
 * @param field - Cell text
 * @returns The field as it goes on the record.
 */
const quoteField = (field: string): string =>
  /[",\r\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field;

/**
 * Serializes the matrix as CSV text.
 * @param matrix - Header and rows from `buildExportMatrix`
 * @returns CSV text starting with the BOM; one CRLF-terminated record per
 * row, the header first.
 */
export const toCsv = (matrix: ExportMatrix): string => {
  const records = [matrix.columns.map(column => column.header), ...matrix.rows];
  return (
    UTF8_BOM +
    records.map(record => record.map(quoteField).join(",") + CRLF).join("")
  );
};
